import React, { useEffect, useMemo } from 'react';
import { Card, Form, Button } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm, FormProvider, Controller, useFormContext } from 'react-hook-form';
import { useAppDispatch } from '../../../hooks';
import { RootState } from '../../../store';
import RecordResultMessage from '../../../components/common/recordResult';
import { Form as GrassrootzForm } from '../../../components';
import { entityTypes, uploadTypes } from '../../../constants/imageUpload';
import {
  ICampaignOrganizer,
  CampaignOrganizerPriority,
  campaignOrganizerPriorities,
} from '../../../models/CampaignOrganizer';
import {
  clearCampaignOrganizerErrors,
  createCampaignOrganizer,
  updateCampaignOrganizer,
} from '../store/campaignOrganizerActions';
import { systemSettingsRoutes } from '../../systemSettings/constants';

interface Props {
  mode: 'create' | 'edit';
  initialValue?: ICampaignOrganizer | null;
}

interface FormValues {
  name: string;
  mainImagePath: string | null;
  priority: CampaignOrganizerPriority;
}

const defaultValues = (initial?: ICampaignOrganizer | null): FormValues => ({
  name: initial?.name ?? '',
  mainImagePath: initial?.mainImagePath ?? null,
  priority: initial?.priority ?? 'low',
});

const CampaignOrganizerInput: React.FC<Props> = ({ mode, initialValue }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const state = useSelector((s: RootState) => (s as any).campaignOrganizer);

  const initialRecord = useMemo(() => {
    const maybe = initialValue as any;
    return maybe && maybe.data ? (maybe.data as ICampaignOrganizer) : (maybe as ICampaignOrganizer | null) ?? null;
  }, [initialValue]);

  const methods = useForm<FormValues>({
    defaultValues: defaultValues(initialRecord),
    mode: 'onBlur',
  });

  // Reset form when an edit record arrives asynchronously. `methods` is stable
  // across renders so we intentionally only depend on initialRecord.
  useEffect(() => {
    if (initialRecord) {
      methods.reset(defaultValues(initialRecord));
    }
  }, [initialRecord, methods]);

  const onSubmit = methods.handleSubmit((values) => {
    const payload: ICampaignOrganizer = {
      name: values.name.trim(),
      mainImagePath: values.mainImagePath || null,
      priority: values.priority,
    };
    if (mode === 'create') {
      dispatch(createCampaignOrganizer(payload) as any);
    } else if (mode === 'edit' && initialRecord?.id) {
      dispatch(updateCampaignOrganizer(initialRecord.id, payload) as any);
    }
  });

  // Navigate back after a successful save — but only after the user actually
  // submitted. The GET-success handler also sets `success.show = true` on
  // initial edit hydration, which would otherwise bounce us straight back to
  // the list before the form renders.
  const recordSlice = mode === 'create' ? state?.recordCreate : state?.recordEdit;
  const submitted = methods.formState.isSubmitted;
  useEffect(() => {
    if (submitted && recordSlice?.ready === 0 && recordSlice?.success?.show) {
      navigate(systemSettingsRoutes.CAMPAIGN_ORGANIZERS);
    }
  }, [recordSlice, navigate, submitted]);

  // Suppress success banner on initial GET so it only shows after submit.
  const recordToShow = useMemo(() => {
    if (submitted) return recordSlice;
    if (recordSlice?.success?.show) {
      return { ...recordSlice, success: { ...(recordSlice.success || {}), show: false } };
    }
    return recordSlice;
  }, [recordSlice, submitted]);

  return (
    <FormProvider {...methods}>
      <RecordResultMessage
        record={recordToShow}
        onDismiss={() =>
          dispatch(
            clearCampaignOrganizerErrors(mode === 'create' ? 'recordCreate' : 'recordEdit') as any
          )
        }
      />
      <Card fluid style={{ backgroundColor: 'var(--card-subtle-bg)' }}>
        <Card.Content>
          <Form>
            <NameField />
            <ImageField />
            <PriorityField />
          </Form>
        </Card.Content>
      </Card>
      <Button primary onClick={onSubmit}>
        <Translate value='campaignOrganizers.page.input.save' />
      </Button>
      <Button onClick={() => navigate(systemSettingsRoutes.CAMPAIGN_ORGANIZERS)}>
        <Translate value='campaignOrganizers.page.input.cancel' />
      </Button>
    </FormProvider>
  );
};

const NameField: React.FC = () => {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      name='name'
      control={control}
      rules={{
        required: true,
        // Reject whitespace-only values; the submit handler trims and an
        // all-whitespace name would otherwise pass `minLength: 1` then submit
        // as an empty string.
        validate: (v) => typeof v === 'string' && v.trim().length > 0,
      }}
      render={({ field, fieldState: { error } }) => (
        <Form.Field required error={!!error}>
          <label><Translate value='campaignOrganizers.page.input.name' /></label>
          <input
            id='campaignOrganizerName'
            type='text'
            value={field.value || ''}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
          />
        </Form.Field>
      )}
    />
  );
};

const ImageField: React.FC = () => {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      name='mainImagePath'
      control={control}
      rules={{
        validate: (v) => typeof v === 'string' && v.trim().length > 0,
      }}
      render={({ field, fieldState: { error } }) => (
        <Form.Field required error={!!error}>
          <GrassrootzForm.ImageUploadInput
            id='campaignOrganizerMainImagePath'
            label={I18n.t('campaignOrganizers.page.input.mainImage')}
            url={field.value || null}
            entity={entityTypes.ORGANIZATION}
            uploadType={uploadTypes.LOGO}
            position='left'
            width={350}
            height={350}
            onChange={(url?: string) => field.onChange(url ?? null)}
          />
        </Form.Field>
      )}
    />
  );
};

const PriorityField: React.FC = () => {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      name='priority'
      control={control}
      rules={{ required: true }}
      render={({ field }) => (
        <Form.Field required>
          <label><Translate value='campaignOrganizers.page.input.priority.label' /></label>
          <Form.Select
            id='campaignOrganizerPriority'
            fluid
            value={field.value}
            onChange={(_e, { value }) => field.onChange(value as CampaignOrganizerPriority)}
            options={campaignOrganizerPriorities.map((p) => ({
              key: p,
              value: p,
              text: I18n.t(`campaignOrganizers.page.input.priority.options.${p}.label`),
            }))}
          />
        </Form.Field>
      )}
    />
  );
};

export default CampaignOrganizerInput;
